import React, { useCallback } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useAllReportBeneficiariesFromReportWizard } from 'shared/apollo/pageHooks/useAllReportBeneficiariesFromReportWizard';
import {
  AnswerInput,
  HelperText,
  useProgram,
} from 'shared/providers/ProgramProvider';
import PrimaryBeneficiary from 'npo/pages/ReportBuilder/Components/ImpactSummary/PrimaryBeneficiary';
import { GuidanceTextResponse } from 'npo/pages/ReportBuilder/Components/utils';

const emptyGuidance: GuidanceTextResponse = {};

interface PrimaryBeneficiaryFieldProps {
  sectionKey: string | null | undefined;
  questionId: number;
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
}

const StyledDiv = styled.div`
  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
`;

// Q40004 "Who or what does your program primarily help?" — wraps the
// ReportBuilder PrimaryBeneficiary selector (suggested term derived from
// the Q40003 impact model + the shared beneficiary tag list) for the Beta
// program-question / snapshot flow. Persists the chosen term on `text`.
const PrimaryBeneficiaryField: React.FC<PrimaryBeneficiaryFieldProps> = ({
  sectionKey,
  questionId,
  value,
  onChange,
  disabled,
}) => {
  const { data: beneficiaryTags, loading } =
    useAllReportBeneficiariesFromReportWizard();
  const { answersArray } = useProgram();

  const handleChange = useCallback(
    (_questionId: number, next: string) => {
      onChange({ ...value, text: next || null, questionText: next || null });
    },
    [onChange, value],
  );

  if (loading && (!beneficiaryTags || beneficiaryTags.length === 0)) {
    return <TrueImpactLoading />;
  }

  if (!beneficiaryTags || beneficiaryTags.length === 0) {
    return (
      <TrueImpactText color="critical" variant="body-small">
        No beneficiary terms are available yet.
      </TrueImpactText>
    );
  }

  return (
    <StyledDiv className={disabled ? 'disabled' : undefined}>
      <HelperText sectionKey={sectionKey} questionId={questionId} />
      <PrimaryBeneficiary
        data={beneficiaryTags}
        questionId={questionId}
        answers={answersArray}
        value={value.text ?? undefined}
        onChange={handleChange}
        guidanceText={emptyGuidance}
      />
    </StyledDiv>
  );
};

export default PrimaryBeneficiaryField;
